import React, { useState, useEffect } from 'react';
import { Asset } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { market } from '../services/api';

interface Props {
  assets: Asset[];
  onSelectSymbol: (symbol: string) => void;
  onRefreshAssets?: () => void;
}

export const AssetDirectory: React.FC<Props> = ({ assets, onSelectSymbol, onRefreshAssets }) => {
  const { language } = useLanguage();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);

  // Form inputs
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [exchange, setExchange] = useState('NASDAQ');
  const [sector, setSector] = useState('Technology');
  const [dividendYield, setDividendYield] = useState('0.00');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // ISIN Auto-Lookup
  const [isinQuery, setIsinQuery] = useState('');
  const [isinLoading, setIsinLoading] = useState(false);
  const [isinSuccess, setIsinSuccess] = useState('');

  // Filters & sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorFilter, setSectorFilter] = useState('ALL');
  const [eurUsdRate, setEurUsdRate] = useState(1.09);

  useEffect(() => {
    market.getFxRate()
      .then(res => {
        if (res.data && res.data.rate) {
          setEurUsdRate(res.data.rate);
        }
      })
      .catch(err => {
        console.error('Failed to load exchange rate in AssetDirectory', err);
      });
  }, []);

  const sectorsList = Array.from(new Set(assets.map(a => a.sector || 'General'))).sort();

  const filteredAssets = assets.filter(a => {
    const matchesSearch = (a.symbol || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (a.name && a.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (a.exchange && a.exchange.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesSector = sectorFilter === 'ALL' || (a.sector || 'General') === sectorFilter;

    return matchesSearch && matchesSector;
  });

  const handleIsinLookup = async () => {
    if (!isinQuery) return;
    setIsinLoading(true);
    setError('');
    setIsinSuccess('');
    try {
      const res = await market.resolveIsin(isinQuery.trim());
      if (res.data && res.data.symbol) {
        setSymbol(res.data.symbol);
        setName(res.data.name || '');
        setExchange(res.data.exchange || 'NASDAQ');
        setIsinSuccess(language === 'sk' 
          ? `Nájdené: ${res.data.symbol} (${res.data.name})`
          : `Found: ${res.data.symbol} (${res.data.name})`
        );
      }
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || (language === 'sk' ? 'ISIN sa nepodarilo nájsť.' : 'Failed to resolve ISIN.'));
    } finally {
      setIsinLoading(false);
    }
  };

  const handleOpenEdit = (asset: Asset) => {
    setEditingAsset(asset);
    setSymbol(asset.symbol);
    setName(asset.name || '');
    setCurrency(asset.currency || 'USD');
    setExchange(asset.exchange || 'NASDAQ');
    setSector(asset.sector || 'General');
    setShowAddForm(true);
    setError('');
    setSuccess('');
  };

  const resetForm = () => {
    setSymbol('');
    setName('');
    setCurrency('USD');
    setExchange('NASDAQ');
    setSector('Technology');
    setDividendYield('0.00');
    setIsinQuery('');
    setIsinSuccess('');
    setEditingAsset(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!symbol || !name) {
      setError(language === 'sk' ? 'Symbol a názov sú povinné.' : 'Symbol and Name are required.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (editingAsset) {
        await market.updateAsset(editingAsset.id, {
          symbol: symbol.toUpperCase(),
          name,
          currency,
          exchange,
          sector,
          dividendYield: parseFloat(dividendYield) || 0
        });
        setSuccess(language === 'sk' ? 'Aktívum úspešne upravené!' : 'Asset updated successfully!');
      } else {
        await market.createAsset({
          symbol: symbol.toUpperCase(),
          name,
          currency,
          exchange,
          sector,
          dividend_yield: parseFloat(dividendYield) || 0
        });
        setSuccess(language === 'sk' ? 'Nové aktívum zaregistrované!' : 'New asset registered!');
      }

      resetForm();

      if (onRefreshAssets) {
        onRefreshAssets();
      }
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || (language === 'sk' ? 'Operácia zlyhala.' : 'Operation failed.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAsset = async (id: string, sym: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(language === 'sk' ? `Naozaj chcete vymazať ${sym} z databázy?` : `Are you sure you want to remove ${sym} from directory?`)) {
      return;
    }

    try {
      await market.deleteAsset(id);
      setSuccess(language === 'sk' ? `Aktívum ${sym} vymazané.` : `Asset ${sym} removed.`);
      if (onRefreshAssets) {
        onRefreshAssets();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete asset.');
    }
  };

  return (
    <div className="bg-white border border-[#1c1917]/10 rounded-3xl p-4 md:p-6 shadow-sm shadow-slate-100 font-mono text-xs space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-stone-200/80 pb-4">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
            <span>📁</span>
            {language === 'sk' ? 'Správa & Adresár Aktív' : 'Asset Management Directory'}
          </h3>
          <p className="text-[10px] text-stone-500 mt-0.5">
            {language === 'sk' 
              ? 'Pridávanie, úprava a správa sledovaných akciových titulov' 
              : 'Workflow for adding, editing, and managing active tracked tickers'}
          </p>
        </div>

        <button
          onClick={() => {
            if (showAddForm) {
              resetForm();
            } else {
              setShowAddForm(true);
            }
          }}
          className="bg-[#1c1917] text-white hover:bg-stone-850 font-extrabold text-[10px] px-3.5 py-2 rounded-xl transition-all shadow-sm flex items-center gap-1.5"
        >
          <span>{showAddForm ? '✕' : '➕'}</span>
          {showAddForm 
            ? (language === 'sk' ? 'ZATVORIŤ' : 'CANCEL') 
            : (language === 'sk' ? 'PRIDAŤ NOVÉ AKTÍVUM' : 'ADD NEW ASSET')}
        </button>
      </div>

      {error && (
        <div className="p-3 border border-rose-200 bg-rose-50 text-rose-700 font-bold rounded-xl text-[11px]">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 border border-emerald-200 bg-emerald-50 text-emerald-800 font-bold rounded-xl text-[11px]">
          ✓ {success}
        </div>
      )}

      {/* Asset Form Component */}
      {showAddForm && (
        <form onSubmit={handleSubmit} className="p-5 bg-stone-50 border border-stone-200 rounded-2xl space-y-4 animate-[fadeIn_0.2s_ease-out]">
          <div className="flex justify-between items-center border-b border-stone-200 pb-2">
            <h4 className="font-extrabold uppercase text-stone-900 text-xs flex items-center gap-1.5">
              <span>{editingAsset ? '✏️' : '➕'}</span>
              {editingAsset 
                ? (language === 'sk' ? `Úprava Aktíva (${editingAsset.symbol})` : `Edit Asset (${editingAsset.symbol})`)
                : (language === 'sk' ? 'Registrácia Nového Aktíva' : 'Register New Market Asset')}
            </h4>
            <button type="button" onClick={resetForm} className="text-stone-400 hover:text-stone-700 font-bold">✕</button>
          </div>

          {!editingAsset && (
            <div className="bg-white border border-stone-200 rounded-xl p-3 space-y-2">
              <label className="block text-[9px] text-stone-500 font-bold uppercase">
                {language === 'sk' ? 'Vyhľadať podľa ISIN (Yahoo Finance)' : 'Auto-fill by ISIN Code (Yahoo Finance)'}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="e.g. US0378331005"
                  value={isinQuery}
                  onChange={(e) => setIsinQuery(e.target.value)}
                  className="flex-1 bg-stone-50 border border-stone-300 rounded-xl px-3 py-1.5 outline-none uppercase font-bold text-xs"
                />
                <button
                  type="button"
                  onClick={handleIsinLookup}
                  disabled={isinLoading || !isinQuery}
                  className="bg-stone-900 text-white font-bold text-[10px] px-4 py-1.5 rounded-xl hover:bg-stone-800 transition-all disabled:opacity-50"
                >
                  {isinLoading ? '...' : (language === 'sk' ? 'VYHĽADAŤ' : 'RESOLVE ISIN')}
                </button>
              </div>
              {isinSuccess && (
                <p className="text-[10px] text-emerald-600 font-bold">
                  ✓ {isinSuccess}
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[9px] text-stone-500 font-bold uppercase mb-1">TICKER SYMBOL</label>
              <input
                type="text"
                placeholder="AAPL / CSG"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                className="w-full bg-white border border-stone-300 rounded-xl px-3 py-2 outline-none font-extrabold text-xs"
                required
              />
            </div>

            <div>
              <label className="block text-[9px] text-stone-500 font-bold uppercase mb-1">COMPANY NAME</label>
              <input
                type="text"
                placeholder="Apple Inc."
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-stone-300 rounded-xl px-3 py-2 outline-none text-xs"
                required
              />
            </div>

            <div>
              <label className="block text-[9px] text-stone-500 font-bold uppercase mb-1">CURRENCY</label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full bg-white border border-stone-300 rounded-xl px-3 py-2 outline-none font-bold text-xs"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[9px] text-stone-500 font-bold uppercase mb-1">EXCHANGE</label>
              <input
                type="text"
                placeholder="NASDAQ / Euronext"
                value={exchange}
                onChange={(e) => setExchange(e.target.value)}
                className="w-full bg-white border border-stone-300 rounded-xl px-3 py-2 outline-none text-xs"
              />
            </div>

            <div>
              <label className="block text-[9px] text-stone-500 font-bold uppercase mb-1">SECTOR</label>
              <input
                type="text"
                placeholder="Technology / Healthcare"
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className="w-full bg-white border border-stone-300 rounded-xl px-3 py-2 outline-none text-xs"
              />
            </div>

            <div>
              <label className="block text-[9px] text-stone-500 font-bold uppercase mb-1">DIVIDEND YIELD (%)</label>
              <input
                type="number"
                step="any"
                placeholder="2.50"
                value={dividendYield}
                onChange={(e) => setDividendYield(e.target.value)}
                className="w-full bg-white border border-stone-300 rounded-xl px-3 py-2 outline-none text-xs font-bold"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-stone-300 hover:bg-stone-200/60 rounded-xl font-bold text-stone-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 bg-stone-900 hover:bg-stone-800 text-white font-extrabold rounded-xl transition-all shadow-sm"
            >
              {loading ? 'Processing...' : (editingAsset ? 'Save Asset Changes' : 'Register Asset')}
            </button>
          </div>
        </form>
      )}

      {/* Directory Filter Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
        <div className="sm:col-span-8 relative">
          <input
            type="text"
            placeholder={language === 'sk' ? 'Vyhľadať v zozname aktív...' : 'Search ticker or company...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#fbfaf7] border border-[#1c1917]/20 rounded-xl px-3.5 py-2 text-xs outline-none focus:border-[#1c1917] transition-all font-mono"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2 text-stone-400 hover:text-stone-600 font-bold"
            >
              ×
            </button>
          )}
        </div>

        <div className="sm:col-span-4">
          <select
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            className="w-full bg-[#fbfaf7] border border-[#1c1917]/20 rounded-xl px-3 py-2 text-xs outline-none font-bold text-stone-800"
          >
            <option value="ALL">All Sectors ({assets.length})</option>
            {sectorsList.map(sec => (
              <option key={sec} value={sec}>{sec}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Desktop Asset List Table */}
      <div className="hidden sm:block overflow-x-auto border border-stone-200/80 rounded-2xl">
        <table className="w-full text-left border-collapse font-mono">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-stone-600 font-extrabold text-[9px] uppercase">
              <th className="p-3">Ticker / Symbol</th>
              <th className="p-3">Company Name</th>
              <th className="p-3">Exchange & Sector</th>
              <th className="p-3 text-right">Price (EUR)</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAssets.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-stone-400">
                  {language === 'sk' ? 'Nenašli sa žiadne vyhovujúce aktíva' : 'No assets match filter criteria'}
                </td>
              </tr>
            ) : (
              filteredAssets.map((asset) => {
                const isEur = asset.currency === 'EUR';
                const priceInEur = isEur ? (asset.current_price || 0) : (asset.current_price || 0) / eurUsdRate;

                return (
                  <tr 
                    key={asset.id} 
                    className="border-b border-stone-100 hover:bg-stone-50/80 transition-colors cursor-pointer group"
                    onClick={() => onSelectSymbol(asset.symbol)}
                  >
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="font-extrabold text-stone-900 text-sm group-hover:text-indigo-600 transition-colors">
                          {asset.symbol}
                        </span>
                        <span className={`text-[8px] font-extrabold px-1.5 py-0.5 rounded border ${
                          asset.currency === 'EUR' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-sky-50 text-sky-800 border-sky-200'
                        }`}>
                          {asset.currency}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="font-semibold text-stone-800 block text-xs">{asset.name}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-stone-500 text-[10px] block font-medium">
                        {asset.exchange || 'NASDAQ'} • {asset.sector || 'General'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="font-extrabold text-stone-900 block text-xs">
                        €{Number(priceInEur).toFixed(2)}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEdit(asset);
                          }}
                          className="text-stone-600 hover:text-stone-900 font-bold px-2 py-1 rounded-lg border border-stone-200 hover:border-stone-400 bg-white transition-all text-[10px]"
                          title="Edit asset details"
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={(e) => handleDeleteAsset(asset.id, asset.symbol, e)}
                          className="text-rose-600 hover:text-rose-800 font-bold px-2 py-1 rounded-lg border border-rose-200 hover:border-rose-400 bg-rose-50 transition-all text-[10px]"
                          title="Delete asset from directory"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View (Phones < 640px) */}
      <div className="block sm:hidden space-y-2.5 font-mono">
        {filteredAssets.length === 0 ? (
          <div className="p-6 text-center text-stone-400 border border-dashed border-stone-200 rounded-xl">
            {language === 'sk' ? 'Nenašli sa žiadne vyhovujúce aktíva' : 'No assets match filter criteria'}
          </div>
        ) : (
          filteredAssets.map((asset) => {
            const isEur = asset.currency === 'EUR';
            const priceInEur = isEur ? (asset.current_price || 0) : (asset.current_price || 0) / eurUsdRate;

            return (
              <div
                key={asset.id}
                onClick={() => onSelectSymbol(asset.symbol)}
                className="p-3.5 border border-stone-200 rounded-xl bg-stone-50/50 space-y-2 cursor-pointer active:scale-[0.99] transition-all"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-extrabold text-stone-900 text-sm">{asset.symbol}</span>
                      <span className={`text-[8px] font-extrabold px-1.5 py-0.2 rounded border ${
                        asset.currency === 'EUR' ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-sky-50 text-sky-800 border-sky-200'
                      }`}>
                        {asset.currency}
                      </span>
                    </div>
                    <div className="text-stone-500 text-[10px] mt-0.5 truncate max-w-[190px]">{asset.name}</div>
                  </div>
                  <div className="text-right">
                    <span className="text-[9px] uppercase font-bold text-stone-400 block">Cena</span>
                    <span className="font-extrabold text-stone-900 text-sm">€{Number(priceInEur).toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2 border-t border-stone-200/60 text-[10px]">
                  <span className="text-stone-400 truncate max-w-[140px]">{asset.sector || 'General'}</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEdit(asset);
                      }}
                      className="px-2 py-0.5 rounded border border-stone-300 bg-white font-bold text-stone-700"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={(e) => handleDeleteAsset(asset.id, asset.symbol, e)}
                      className="px-2 py-0.5 rounded border border-rose-200 bg-rose-50 font-bold text-rose-700"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
